import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Row, Table, Typography } from 'antd'
import Chart from 'react-apexcharts'
import { createUseStyles } from 'react-jss'
import dayjs from '../../lib/datetime'
import ChartTableSwitch from './ChartTableSwitch'

const { Title } = Typography

export default function PatientCreationStats({ stats }) {
  const [viewMode, setViewMode] = useState('chart')
    const classes = createUseStyles({
    inlineTitle: {
      display: 'inline-block',
    }
  })()

  const chartOptions = useMemo(() => {
    const colors = ['#3695dc']
    return {
      chart: {
        type: 'line',
        sparkline: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        stacked: false,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'straight',
        width: 2,
      },
      fill: {
        type: 'solid',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      states: {
        normal: {
          filter: {
            type: 'none',
          },
        },
      },
      grid: {
        show: false,
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        clusterGroupedSeries: false,
      },
      markers: { size: [5, 0, 0, 0], shape: 'square' },
      xaxis: {
        categories: stats.map(stat => dayjs(`${stat.year}-${String(stat.month).padStart(2, '0')}-01`).format('MMM YY')),
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        type: 'category',
      },
      yaxis: {
        labels: {
          formatter(value) {
            return `${Math.round(value)}`
          },
        },
        min: 0,
        max: stats.reduce((max, stat) => Math.max(max, stat.count), 0),
        title: {
          text: 'Patient Count',
        },
      },
      colors,
      tooltip: {
        y: {
          formatter(value) {
            return `${Math.round(value)}`
          },
        },
      },
    }
  }, [stats])

  const chartSeries = [
    {
      name: 'Patients Created',
      data: stats.map(stat => stat.count),
      type: 'line',
    },
  ]

  const tableColumns = [
    {
      key: 'date',
      title: 'Date',
      render: ({ year, month }) => dayjs(`${year}-${String(month).padStart(2, '0')}-01`).format('MMMM YYYY'),
    },
    {
      key: 'count',
      title: 'Patient Count',
      dataIndex: 'count',
      align: 'center',
    },
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} className={classes.inlineTitle}>
            Patients created
          </Title>
        </Col>
        <Col>
          <ChartTableSwitch onChange={setViewMode} viewMode={viewMode} />
        </Col>
      </Row>

      {viewMode === 'chart' && (
        <Row gutter={[0, 50]}>
          <Col span={24}>
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={350}
            />
          </Col>
        </Row>
      )}

      {viewMode === 'table' && (
        <Row gutter={[0, 50]}>
          <Col span={24}>
            <Table
              columns={tableColumns}
              dataSource={stats.map((stat, idx) => ({ ...stat, key: idx }))}
              size="small"
              pagination={false}
            />
          </Col>
        </Row>
      )}
    </>
  )
}

PatientCreationStats.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
}
